// ============================================================================
// VALIDATION RULES - 25+ rules for system design validation
// ============================================================================

import { ValidationRule, RuleSet } from './types';

export const VALIDATION_RULES: ValidationRule[] = [
  // ===========================================================================
  // PROTOCOL COMPATIBILITY RULES (proto-*)
  // ===========================================================================
  {
    id: 'proto-001',
    version: '1.0.0',
    name: 'SQL Protocol Requires Database Target',
    description: 'SQL protocol can only connect to relational or timeseries databases',
    category: 'protocol_compatibility',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          protocol: 'sql',
          targetSubtypeNot: ['db.relational', 'db.timeseries'],
        },
      },
    ],
    messageTemplate: 'SQL connection targets {{targetNode}}, which is not a SQL database. SQL protocol requires relational or timeseries databases.',
    fixes: [
      { description: 'Change protocol to match target component (e.g., Redis protocol for Redis)', type: 'modify_edge' },
      { description: 'Replace target with a SQL database (PostgreSQL, MySQL, Aurora)', type: 'modify_node' },
    ],
    relatedRules: ['proto-002', 'proto-003'],
    scorePenalty: { dimension: 'correctness', penalty: 25 },
  },
  {
    id: 'proto-002',
    version: '1.0.0',
    name: 'Redis Protocol Requires Cache/KV Target',
    description: 'Redis protocol can only connect to Redis-compatible caches',
    category: 'protocol_compatibility',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          protocol: 'redis',
          targetSubtypeNot: ['cache.kv', 'db.keyvalue'],
        },
      },
    ],
    messageTemplate: 'Redis protocol targets {{targetNode}}, which is not a Redis-compatible store.',
    fixes: [
      { description: 'Change to Redis or ElastiCache component', type: 'modify_node' },
      { description: 'Use HTTPS protocol if target has REST API', type: 'modify_edge', autoFixParams: { newProtocol: 'https' } },
    ],
    relatedRules: ['proto-001'],
    scorePenalty: { dimension: 'correctness', penalty: 25 },
  },
  {
    id: 'proto-003',
    version: '1.0.0',
    name: 'Kafka Protocol Requires Streaming Target',
    description: 'Kafka protocol requires Kafka or compatible streaming platform',
    category: 'protocol_compatibility',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          protocol: 'kafka',
          targetSubtypeNot: ['queue.stream', 'queue.pubsub'],
        },
      },
    ],
    messageTemplate: 'Kafka protocol targets {{targetNode}}, which is not a streaming platform.',
    fixes: [
      { description: 'Add Kafka, Kinesis, or Pulsar as target', type: 'modify_node' },
    ],
    relatedRules: ['proto-004'],
    scorePenalty: { dimension: 'correctness', penalty: 25 },
  },
  {
    id: 'proto-004',
    version: '1.0.0',
    name: 'AMQP Protocol Requires Message Queue',
    description: 'AMQP protocol requires RabbitMQ or compatible message broker',
    category: 'protocol_compatibility',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          protocol: 'amqp',
          targetSubtypeNot: ['queue.fifo', 'queue.pubsub', 'queue.task'],
        },
      },
    ],
    messageTemplate: 'AMQP protocol targets {{targetNode}}, which is not a message queue.',
    fixes: [
      { description: 'Replace with RabbitMQ or similar message broker', type: 'modify_node' },
    ],
    relatedRules: ['proto-003'],
    scorePenalty: { dimension: 'correctness', penalty: 25 },
  },
  {
    id: 'proto-005',
    version: '1.0.0',
    name: 'S3 Protocol Requires Object Storage',
    description: 'S3 protocol can only connect to object storage services',
    category: 'protocol_compatibility',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          protocol: 's3',
          targetSubtypeNot: ['storage.object'],
        },
      },
    ],
    messageTemplate: 'S3 protocol targets {{targetNode}}, which is not object storage.',
    fixes: [
      { description: 'Replace target with S3, GCS, or Azure Blob Storage', type: 'modify_node' },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'correctness', penalty: 25 },
  },

  // ===========================================================================
  // DIRECTIONALITY RULES (dir-*)
  // ===========================================================================
  {
    id: 'dir-001',
    version: '1.0.0',
    name: 'Client Cannot Be Connection Target',
    description: 'Clients initiate connections; they cannot receive incoming connections',
    category: 'directionality',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          targetCategory: 'client',
        },
      },
    ],
    messageTemplate: '{{sourceNode}} connects to {{targetNode}}, but clients cannot receive connections. Clients only initiate requests.',
    fixes: [
      { description: 'Reverse the connection direction', type: 'modify_edge' },
      { description: 'Replace client with a server component', type: 'modify_node' },
    ],
    relatedRules: ['dir-002'],
    scorePenalty: { dimension: 'correctness', penalty: 30 },
  },
  {
    id: 'dir-002',
    version: '1.0.0',
    name: 'Storage Cannot Initiate Connections',
    description: 'Storage components respond to queries but do not initiate connections',
    category: 'directionality',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          sourceCategory: ['database', 'storage'],
          semanticNot: ['replication', 'observability', 'encryption'],
          // Also exclude connections to security components (KMS, etc.)
          targetCategoryNot: ['security'],
        },
      },
    ],
    messageTemplate: '{{sourceNode}} initiates connection to {{targetNode}}, but storage/database components do not initiate connections (except for replication or encryption).',
    fixes: [
      { description: 'Reverse the connection direction', type: 'modify_edge' },
      { description: 'Mark as replication connection if syncing data', type: 'modify_edge', autoFixParams: { newSemantic: 'replication' } },
    ],
    relatedRules: ['dir-001'],
    scorePenalty: { dimension: 'correctness', penalty: 20 },
  },
  {
    id: 'dir-003',
    version: '1.0.0',
    name: 'Broker Push Pattern',
    description: 'Message brokers receive messages and deliver to subscribers',
    category: 'directionality',
    severity: 'warning',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          sourceCategory: 'queue',
          targetCategory: ['compute', 'gateway'],
          semanticNot: ['async_event', 'streaming'],
        },
      },
    ],
    messageTemplate: '{{sourceNode}} (broker) connects to {{targetNode}} with request/response. Brokers typically push events, not make requests.',
    fixes: [
      { description: 'Change semantic to async_event or streaming', type: 'modify_edge' },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'correctness', penalty: 10 },
  },

  // ===========================================================================
  // ANTI-PATTERN RULES (anti-*)
  // ===========================================================================
  {
    id: 'anti-001',
    version: '1.0.0',
    name: 'Database Behind CDN',
    description: 'CDN should cache static content, not database responses',
    category: 'anti_pattern',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'path_exists',
        params: {
          fromSubtype: 'cache.cdn',
          toCategory: 'database',
          maxHops: 1,
        },
      },
    ],
    messageTemplate: 'CDN directly connects to database. CDNs are for static content; database queries should go through your application layer.',
    fixes: [
      { description: 'Add application server between CDN and database', type: 'add_node' },
      { description: 'Remove CDN→Database connection', type: 'remove_edge' },
    ],
    relatedRules: ['anti-005'],
    scorePenalty: { dimension: 'correctness', penalty: 30 },
  },
  {
    id: 'anti-002',
    version: '1.0.0',
    name: 'Cross-Database Direct Connection',
    description: 'Databases should not directly query each other without ETL/CDC',
    category: 'anti_pattern',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          sourceCategory: 'database',
          targetCategory: 'database',
          semanticNot: ['replication'],
        },
      },
    ],
    messageTemplate: '{{sourceNode}} directly connects to {{targetNode}}. Cross-database queries require an ETL/CDC component.',
    fixes: [
      { description: 'Add ETL/CDC pipeline (Debezium, Airbyte) between databases', type: 'add_node' },
      { description: 'If syncing same data, use database replication semantic', type: 'modify_edge' },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'correctness', penalty: 30 },
  },
  {
    id: 'anti-003',
    version: '1.0.0',
    name: 'Queue Used as Database',
    description: 'Message queues should not be the source of truth for data',
    category: 'anti_pattern',
    severity: 'warning',
    enabled: true,
    conditions: [
      {
        type: 'node_exists',
        params: { category: 'queue' },
      },
      {
        type: 'node_missing',
        params: { category: 'database' },
      },
    ],
    messageTemplate: 'Queue present without any database. Queues are for async messaging, not persistence.',
    fixes: [
      { description: 'Add a database for persistent storage', type: 'add_node' },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'reliability', penalty: 20 },
  },
  {
    id: 'anti-004',
    version: '1.0.0',
    name: 'Too Many Synchronous Hops',
    description: 'Long synchronous call chains increase latency and failure risk',
    category: 'anti_pattern',
    severity: 'warning',
    enabled: true,
    conditions: [
      {
        type: 'graph_property',
        params: {
          property: 'maxSyncChainDepth',
          operator: '>',
          value: 5,
        },
      },
    ],
    messageTemplate: 'Synchronous call chain is too deep. Consider async processing for chains > 5 hops.',
    fixes: [
      { description: 'Add message queue to break sync chain', type: 'add_node' },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'latency', penalty: 15 },
  },
  {
    id: 'anti-005',
    version: '1.0.0',
    name: 'Client Direct to Database',
    description: 'Clients should not connect directly to databases',
    category: 'anti_pattern',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          sourceCategory: 'client',
          targetCategory: 'database',
        },
      },
    ],
    messageTemplate: '{{sourceNode}} connects directly to {{targetNode}}. Clients should access data through an API layer.',
    fixes: [
      { description: 'Add API Gateway or application server between client and database', type: 'add_node' },
    ],
    relatedRules: ['anti-001', 'sec-003'],
    scorePenalty: { dimension: 'security', penalty: 30 },
  },

  // ===========================================================================
  // REQUIRED COMPONENTS RULES (req-*)
  // ===========================================================================
  {
    id: 'req-001',
    version: '1.0.0',
    name: 'Read-Heavy Workload Needs Cache',
    description: 'High read/write ratio workloads benefit from caching',
    category: 'required_components',
    severity: 'warning',
    enabled: true,
    appliesWhen: {
      requirementFilters: { readWriteRatio: 10 } as any,
    },
    conditions: [
      {
        type: 'node_missing',
        params: { category: 'cache' },
      },
    ],
    messageTemplate: 'Read-heavy workload without caching layer. Consider adding Redis or a CDN.',
    fixes: [
      { description: 'Add Redis cache between compute and database', type: 'add_node', autoFixParams: { componentId: 'redis' } },
    ],
    relatedRules: ['req-002'],
    scorePenalty: { dimension: 'latency', penalty: 20 },
  },
  {
    id: 'req-002',
    version: '1.0.0',
    name: 'Public API Needs Rate Limiting',
    description: 'Public-facing APIs should have rate limiting',
    category: 'required_components',
    severity: 'warning',
    enabled: true,
    appliesWhen: {
      requirementFilters: { publicFacing: true },
    },
    conditions: [
      {
        type: 'node_exists',
        params: { category: 'gateway' },
      },
      {
        type: 'node_missing',
        params: { subtype: 'security.waf' },
      },
    ],
    messageTemplate: 'Public-facing API detected without WAF/rate limiting protection.',
    fixes: [
      { description: 'Add API Gateway with rate limiting enabled', type: 'add_node' },
      { description: 'Add WAF with rate limiting rules', type: 'add_node' },
    ],
    relatedRules: ['sec-002'],
    scorePenalty: { dimension: 'security', penalty: 15 },
  },
  {
    id: 'req-003',
    version: '1.0.0',
    name: 'High QPS Needs Load Balancer',
    description: 'High traffic designs need load balancing',
    category: 'required_components',
    severity: 'warning',
    enabled: true,
    appliesWhen: {
      requirementFilters: { qpsRead: 10000 } as any,
    },
    conditions: [
      {
        type: 'node_missing',
        params: { category: 'loadbalancer' },
      },
    ],
    messageTemplate: 'High QPS requirement without load balancer. Single server cannot handle this load.',
    fixes: [
      { description: 'Add Application Load Balancer (ALB)', type: 'add_node', autoFixParams: { componentId: 'alb' } },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'scalability', penalty: 25 },
  },
  {
    id: 'req-004',
    version: '1.0.0',
    name: 'Real-Time Needs WebSocket',
    description: 'Real-time requirements need persistent connections',
    category: 'required_components',
    severity: 'warning',
    enabled: true,
    appliesWhen: {
      requirementFilters: { realTimeRequired: true },
    },
    conditions: [
      {
        type: 'edge_missing',
        params: { protocol: 'websocket' },
      },
      {
        type: 'edge_missing',
        params: { protocol: 'grpc' },
      },
    ],
    messageTemplate: 'Real-time requirement without WebSocket or gRPC streaming connections.',
    fixes: [
      { description: 'Add WebSocket connection from client to server', type: 'add_edge' },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'correctness', penalty: 20 },
  },
  {
    id: 'req-005',
    version: '1.0.0',
    name: 'Auth Required Without Identity Provider',
    description: 'Auth requirements need identity management',
    category: 'required_components',
    severity: 'warning',
    enabled: true,
    appliesWhen: {
      requirementFilters: { authRequired: true },
    },
    conditions: [
      {
        type: 'node_missing',
        params: { subtype: 'security.identity' },
      },
    ],
    messageTemplate: 'Authentication required but no identity provider (Cognito, Auth0) is present.',
    fixes: [
      { description: 'Add identity provider component', type: 'add_node' },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'security', penalty: 15 },
  },

  // ===========================================================================
  // RELIABILITY RULES (rel-*)
  // ===========================================================================
  {
    id: 'rel-001',
    version: '1.0.0',
    name: 'Database Single Point of Failure',
    description: 'Production databases should have replicas',
    category: 'reliability',
    severity: 'warning',
    enabled: true,
    conditions: [
      {
        type: 'node_exists',
        params: {
          category: 'database',
          instanceCount: 1,
        },
      },
    ],
    messageTemplate: '{{node}} is a single point of failure. Consider adding read replicas.',
    fixes: [
      { description: 'Increase instance count or add replica', type: 'modify_node', autoFixParams: { instanceCount: 2 } },
    ],
    relatedRules: ['rel-002'],
    scorePenalty: { dimension: 'reliability', penalty: 20 },
  },
  {
    id: 'rel-002',
    version: '1.0.0',
    name: 'Single Compute Instance',
    description: 'Single compute instance cannot handle failures',
    category: 'reliability',
    severity: 'warning',
    enabled: true,
    appliesWhen: {
      requirementFilters: { availabilitySla: 99.9 } as any,
    },
    conditions: [
      {
        type: 'node_count',
        params: {
          category: 'compute',
          operator: '==',
          value: 1,
        },
      },
    ],
    messageTemplate: 'Only one compute instance for high availability requirement. Add redundancy.',
    fixes: [
      { description: 'Add at least one more compute instance', type: 'modify_node' },
      { description: 'Use auto-scaling group', type: 'modify_node' },
    ],
    relatedRules: ['rel-001'],
    scorePenalty: { dimension: 'reliability', penalty: 25 },
  },
  {
    id: 'rel-003',
    version: '1.0.0',
    name: 'No Health Checks',
    description: 'Load balancers should have health check targets',
    category: 'reliability',
    severity: 'warning',
    enabled: true,
    conditions: [
      {
        type: 'node_exists',
        params: { category: 'loadbalancer' },
      },
      {
        type: 'node_count',
        params: {
          category: 'compute',
          operator: '<',
          value: 2,
        },
      },
    ],
    messageTemplate: 'Load balancer present with single backend. Add more instances for redundancy.',
    fixes: [
      { description: 'Add more compute instances behind load balancer', type: 'add_node' },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'reliability', penalty: 10 },
  },

  // ===========================================================================
  // SECURITY RULES (sec-*)
  // ===========================================================================
  {
    id: 'sec-001',
    version: '1.0.0',
    name: 'Unencrypted External Traffic',
    description: 'External connections must use TLS',
    category: 'security',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'edge_exists',
        params: {
          sourceCategory: 'client',
          protocol: 'http',
        },
      },
    ],
    messageTemplate: 'External traffic uses unencrypted HTTP. Use HTTPS for all external connections.',
    fixes: [
      { description: 'Change protocol to HTTPS', type: 'modify_edge', autoFixParams: { newProtocol: 'https' } },
    ],
    relatedRules: ['sec-002'],
    scorePenalty: { dimension: 'security', penalty: 25 },
  },
  {
    id: 'sec-002',
    version: '1.0.0',
    name: 'Public API Without WAF',
    description: 'Public APIs should have WAF protection',
    category: 'security',
    severity: 'warning',
    enabled: true,
    appliesWhen: {
      requirementFilters: { publicFacing: true },
    },
    conditions: [
      {
        type: 'node_missing',
        params: { subtype: 'security.waf' },
      },
    ],
    messageTemplate: 'Public-facing API without WAF protection. Vulnerable to OWASP Top 10.',
    fixes: [
      { description: 'Add WAF (AWS WAF, Cloudflare) in front of API', type: 'add_node' },
    ],
    relatedRules: ['sec-001'],
    scorePenalty: { dimension: 'security', penalty: 20 },
  },
  {
    id: 'sec-003',
    version: '1.0.0',
    name: 'Database Exposed to Clients',
    description: 'Databases should not be accessible from clients',
    category: 'security',
    severity: 'error',
    enabled: true,
    conditions: [
      {
        type: 'path_exists',
        params: {
          fromCategory: 'client',
          toCategory: 'database',
          maxHops: 2,
          requireIntermediate: false,
        },
      },
    ],
    messageTemplate: 'Database appears to be exposed to clients without proper API layer protection.',
    fixes: [
      { description: 'Add private subnet and API layer', type: 'add_node' },
    ],
    relatedRules: ['anti-005'],
    scorePenalty: { dimension: 'security', penalty: 30 },
  },

  // ===========================================================================
  // OBSERVABILITY RULES (obs-*)
  // ===========================================================================
  {
    id: 'obs-001',
    version: '1.0.0',
    name: 'No Metrics Collection',
    description: 'Production systems need metrics for monitoring',
    category: 'observability',
    severity: 'warning',
    enabled: true,
    conditions: [
      {
        type: 'node_missing',
        params: { subtype: 'monitoring.metrics' },
      },
      {
        type: 'graph_property',
        params: {
          property: 'totalNodes',
          operator: '>',
          value: 3,
        },
      },
    ],
    messageTemplate: 'No metrics collection (Prometheus, CloudWatch) in a system with multiple components.',
    fixes: [
      { description: 'Add Prometheus or CloudWatch for metrics', type: 'add_node' },
    ],
    relatedRules: ['obs-002'],
    scorePenalty: { dimension: 'operability', penalty: 15 },
  },
  {
    id: 'obs-002',
    version: '1.0.0',
    name: 'No Centralized Logging',
    description: 'Distributed systems need centralized logging',
    category: 'observability',
    severity: 'warning',
    enabled: true,
    conditions: [
      {
        type: 'node_missing',
        params: { subtype: 'monitoring.logging' },
      },
      {
        type: 'graph_property',
        params: {
          property: 'totalNodes',
          operator: '>',
          value: 3,
        },
      },
    ],
    messageTemplate: 'No centralized logging (ELK, CloudWatch Logs) for debugging distributed issues.',
    fixes: [
      { description: 'Add ELK stack or cloud logging service', type: 'add_node' },
    ],
    relatedRules: ['obs-001'],
    scorePenalty: { dimension: 'operability', penalty: 15 },
  },
  {
    id: 'obs-003',
    version: '1.0.0',
    name: 'No Distributed Tracing',
    description: 'Microservices need tracing for debugging',
    category: 'observability',
    severity: 'info',
    enabled: true,
    conditions: [
      {
        type: 'node_missing',
        params: { subtype: 'monitoring.tracing' },
      },
      {
        type: 'node_count',
        params: {
          category: 'compute',
          operator: '>',
          value: 2,
        },
      },
    ],
    messageTemplate: 'Multiple services without distributed tracing. Consider Jaeger, X-Ray, or Datadog APM.',
    fixes: [
      { description: 'Add distributed tracing', type: 'add_node' },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'operability', penalty: 10 },
  },

  // ===========================================================================
  // MULTI-REGION RULES (geo-*)
  // ===========================================================================
  {
    id: 'geo-001',
    version: '1.0.0',
    name: 'Multi-Region Without Global LB',
    description: 'Multi-region deployments need global traffic management',
    category: 'multi_region',
    severity: 'warning',
    enabled: true,
    appliesWhen: {
      requirementFilters: { regions: ['us-east-1', 'eu-west-1'] as any },
    },
    conditions: [
      {
        type: 'node_missing',
        params: { subtype: 'lb.global' },
      },
      {
        type: 'node_missing',
        params: { subtype: 'dns.authoritative' },
      },
    ],
    messageTemplate: 'Multi-region deployment without global load balancer or DNS-based routing.',
    fixes: [
      { description: 'Add Global Load Balancer or Route53', type: 'add_node' },
    ],
    relatedRules: ['geo-002'],
    scorePenalty: { dimension: 'reliability', penalty: 20 },
  },
  {
    id: 'geo-002',
    version: '1.0.0',
    name: 'Multi-Region DB Without Replication',
    description: 'Cross-region data needs replication strategy',
    category: 'multi_region',
    severity: 'warning',
    enabled: true,
    appliesWhen: {
      requirementFilters: { multiRegionActiveActive: true },
    },
    conditions: [
      {
        type: 'node_exists',
        params: { category: 'database' },
      },
      {
        type: 'node_count',
        params: {
          category: 'database',
          operator: '==',
          value: 1,
        },
      },
    ],
    messageTemplate: 'Active-active multi-region requires database replication strategy.',
    fixes: [
      { description: 'Add cross-region database replication', type: 'add_edge' },
      { description: 'Use globally distributed database (CockroachDB, Spanner)', type: 'modify_node' },
    ],
    relatedRules: ['geo-001'],
    scorePenalty: { dimension: 'reliability', penalty: 25 },
  },

  // ===========================================================================
  // COST RULES (cost-*)
  // ===========================================================================
  {
    id: 'cost-001',
    version: '1.0.0',
    name: 'Over-Provisioned Design',
    description: 'System capacity significantly exceeds requirements',
    category: 'cost',
    severity: 'info',
    enabled: true,
    conditions: [
      {
        type: 'graph_property',
        params: {
          property: 'capacityRatio',
          operator: '>',
          value: 5,
        },
      },
    ],
    messageTemplate: 'System provisioned with much more capacity than needed. Consider rightsizing.',
    fixes: [
      { description: 'Reduce instance counts or sizes', type: 'modify_node' },
    ],
    relatedRules: [],
    scorePenalty: { dimension: 'cost', penalty: 15 },
  },
];

// Default ruleset
export const DEFAULT_RULESET: RuleSet = {
  id: 'buildex-core-v1',
  name: 'buildex.dev Core Ruleset',
  version: '1.0.0',
  rules: VALIDATION_RULES,
  metadata: {
    author: 'buildex.dev',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    description: 'Core validation rules for system design challenges',
  },
};

// Fast rules for client-side validation (subset that runs quickly)
export const FAST_RULE_IDS = [
  'proto-001',
  'proto-002',
  'proto-003',
  'proto-004',
  'proto-005',
  'dir-001',
  'dir-002',
  'anti-001',
  'anti-002',
  'anti-005',
  'sec-001',
];

export function getFastRules(): ValidationRule[] {
  return VALIDATION_RULES.filter(r => FAST_RULE_IDS.includes(r.id));
}
